import type { ReactNode } from "react";
import { Button, Center, Paper, Stack, Text, Title } from "@mantine/core";
import { usePortalAuth } from "./PortalAuthProvider";
import {
  hasPortalCapability,
  type PortalCapability,
} from "./portalCapabilityPolicy";

interface PortalCapabilityRouteProps {
  capability: PortalCapability;
  children: ReactNode;
  fallback?: ReactNode;
  homeHref?: string;
}

const capabilityDescriptions: Record<PortalCapability, string> = {
  order: "place or review orders",
  viewResults: "view laboratory results",
  downloadPdfs: "download result PDFs",
};

export function PortalCapabilityRoute({
  capability,
  children,
  fallback,
  homeHref = "/",
}: PortalCapabilityRouteProps) {
  const { canOrder, canViewResults, canDownloadPdfs } = usePortalAuth();

  if (
    hasPortalCapability(
      { canOrder, canViewResults, canDownloadPdfs },
      capability,
    )
  ) {
    return <>{children}</>;
  }

  if (fallback !== undefined) {
    return <>{fallback}</>;
  }

  return (
    <Center mih="60vh" px="md">
      <Paper
        maw={520}
        p="xl"
        withBorder
        role="alert"
        aria-labelledby="portal-capability-route-title"
      >
        <Stack align="center" gap="md">
          <Title id="portal-capability-route-title" order={2} ta="center">
            Access unavailable
          </Title>
          <Text c="dimmed" ta="center">
            This account is not currently enabled to{" "}
            {capabilityDescriptions[capability]}. Contact the laboratory if you
            believe this should be available.
          </Text>
          <Button component="a" href={homeHref}>
            Return to dashboard
          </Button>
        </Stack>
      </Paper>
    </Center>
  );
}
